/* eslint-disable react-hooks/rules-of-hooks */
import create, { UseStore } from 'zustand';
import {
  Option,
  OptionType,
  OptionFolder,
  Parameter,
  Product,
  Style,
  FormatType,
  Currency,
  Material,
  MaterialFolder,
  MaterialType,
  BaseMaterial,
  DefaultBackground,
  BOMOptionInstance,
} from 'api';
import {
  setStyle,
  setProduct,
  loadConfiguration,
  updateExtraheightAndOverhang,
} from './styleproduct';
import {
  setDepth,
  setWidth,
  setWallHeight,
  setOverhang,
  setFormat,
  setCurrency,
} from './userstate';
import { persist } from 'zustand/middleware';
import { nanoid } from 'nanoid';
import { revertable } from 'viewer/revertable';
import { APPSTATE_KEY } from 'helpers/constants';
import { useUiState } from 'state/ui-state/ui-state';

export interface UserStateDimensions {
  depth: number;
  width: number;
  wallHeight: number;
}

export interface UserStateMaterials {
  siding: string | undefined;
  trim: string | undefined;
  roofing: string | undefined;
}

// This state stores the users current configuration
export type UserState = {
  id: string;
  dimensions: UserStateDimensions;
  overhang: number;
  openAreaSize?: number;
  // options and option-related-stuff to change later
  options: Array<Option>; // change to option type which saves location
  optionTypes: Array<OptionType>;
  optionFolders?: Array<OptionFolder>;
  parameters: Array<Parameter>;
  format: FormatType;
  currency: Currency;
  materials: UserStateMaterials;
  background?: DefaultBackground;
  bomOnlyOptions: Array<BOMOptionInstance>;
  hasConcretePad: boolean;
};
type ProductMaterials = Product & {
  materials: Array<Material>;
  baseMaterials?: BaseMaterial[];
  materialTypes: Array<MaterialType>;
  materialFolders: Array<MaterialFolder>;
};

type StyleProduct = {
  style?: Style;
  product?: ProductMaterials;
};

type Dicts = {
  dbIdToShapeDiverId?: { [key: string]: string };
  shapeDiverIdToDbId?: { [key: string]: string };
  materialsDbIdToShapeDiverId?: { [key: string]: string };
};

// Type combining selected style / product  and app state
export type AppState = {
  // state
  id: string;
  configurationId: string | undefined;
  uState: UserState | undefined;
  styleProduct: StyleProduct;
  dicts: Dicts;
  // actions
  setStyleById: (styleId: string) => Promise<void>;
  loadConfiguration: (
    configurationId: string,
    productId: string,
    currency: Currency
  ) => Promise<void>;
  setProductById: (productId: string) => Promise<void>;
  setDepth: (depth: number) => void;
  setWidth: (width: number) => void;
  setOpenAreaSize: (openAreaSize: number) => void;
  setHasConcretePad: (hasConcretePad: boolean) => void;
  setOverhang: (overhang: number) => void;
  setWallHeight: (wallHeight: number) => void;
  setFormat: (format: FormatType) => void;
  setCurrency: (currency: Currency) => void;
  setSiding: (siding: string) => void;
  setTrim: (trim: string) => void;
  setRoofing: (roofing: string) => void;
  reset: () => void;
  setDefaultParameters: () => void;
  setDefaultBackground: (background: DefaultBackground) => void;
  updateExtraheightAndOverhang: (
    styleId: string,
    width: number,
    depth: number
  ) => void;
  setBomOnlyOptions: (bomOnlyOptions: Array<BOMOptionInstance>) => void;
};

export const useAppState = create<AppState>(
  persist(
    revertable((set, get) => ({
      id: nanoid(),
      configurationId: undefined,
      styleProduct: { style: undefined, product: undefined },
      dicts: {},
      uState: undefined,
      // Style / Product State
      setStyleById: async (styleId: string) => {
        await setStyle(styleId, get, set);
      },
      loadConfiguration: async (
        configurationId: string,
        productId: string,
        currency: Currency
      ) => {
        await loadConfiguration(configurationId, productId, currency, set);
      },
      setProductById: async (productId: string) => {
        await setProduct(productId, set);
      },
      // User state
      setDepth: (depth: number) => {
        setDepth(depth, get, set);
      },
      setWidth: (width: number) => {
        setWidth(width, get, set);
      },
      setOpenAreaSize: (openAreaSize: number) => {
        set((state: AppState) => {
          return {
            uState: {
              ...state.uState!,
              openAreaSize,
            },
          };
        });
      },
      setHasConcretePad: (hasConcretePad: boolean) => {
        set((state: AppState) => {
          return {
            uState: {
              ...state.uState!,
              hasConcretePad,
            },
          };
        });
      },
      setWallHeight: (height: number) => {
        setWallHeight(height, get, set);
      },
      setOverhang: (overhang: number) => {
        setOverhang(overhang, get, set);
      },
      setFormat: (format: FormatType) => {
        setFormat(format, get, set);
      },
      setCurrency: (currency: Currency) => {
        setCurrency(currency, get, set);
      },
      setSiding: (siding: string) =>
        set((state: AppState) => ({
          uState: {
            ...state.uState!,
            materials: {
              ...state.uState!.materials,
              siding,
            },
          },
        })),
      setTrim: (trim: string) =>
        set((state: AppState) => ({
          uState: {
            ...state.uState!,
            materials: {
              ...state.uState!.materials,
              trim,
            },
          },
        })),
      setRoofing: (roofing: string) =>
        set((state: AppState) => ({
          uState: {
            ...state.uState!,
            materials: {
              ...state.uState!.materials,
              roofing,
            },
          },
        })),
      setDefaultBackground: (background: DefaultBackground) =>
        set((state: AppState) => ({
          uState: {
            ...state.uState!,
            background,
          },
        })),
      reset: () => {
        set({
          id: nanoid(),
          configurationId: undefined,
          styleProduct: { style: undefined, product: undefined },
          uState: undefined,
        });
      },
      setDefaultParameters: () => {
        set((state: AppState) => {
          const { styleProduct } = state;
          const style = styleProduct.style?.defaultConfiguration?.style;
          return style
            ? {
                uState: {
                  ...state.uState!,
                  dimensions: {
                    depth: style.depth,
                    wallHeight: style.wallHeight,
                    width: style.width,
                  },
                  overhang: style.overhang,
                  materials: {
                    siding: style.siding,
                    trim: style.trim,
                    roofing: style.roofing,
                  },
                  background: style.defaultBackground,
                },
              }
            : { uState: state.uState! };
        });
      },
      updateExtraheightAndOverhang: async (
        styleId: string,
        width: number,
        depth: number
      ) => {
        await updateExtraheightAndOverhang(styleId, width, depth, get, set);
      },
      setBomOnlyOptions: (bomOnlyOptions: Array<BOMOptionInstance>) =>
        set((state: AppState) => ({
          uState: {
            ...state.uState!,
            bomOnlyOptions,
          },
        })),
    })),
    {
      name: APPSTATE_KEY,
      onRehydrateStorage: () => {
        return () => {
          useUiState.getState().setRehydrated(); // The returned function will be called after the state rehydration.
        };
      },
    }
  )
) as UseStore<AppState> & {
  revert: () => void;
  reverted: boolean;
  rehydrated: boolean;
};

export function isGarageProduct() {
  return useAppState.getState()?.styleProduct?.product?.id === '8';
}

export function isUrbanPavilionProduct() {
  return useAppState.getState()?.styleProduct?.product?.id === '3';
}
